import { resolveTypingPassages } from "../utils/typingTest";

const REQUEST_TIMEOUT_MS = 5000;
const SAVE_TIMEOUT_MS = 20000;

async function fetchWithTimeout(url, options = {}, timeoutMs = REQUEST_TIMEOUT_MS) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    return await fetch(url, {
      ...options,
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timeoutId);
  }
}

async function parseResponse(response, fallbackMessage) {
  if (response.ok) {
    return response.json();
  }

  let message = fallbackMessage;

  try {
    const data = await response.json();
    if (data?.message) {
      message = data.message;
    }
  } catch (error) {
    // Ignore JSON parse failures and use fallback message.
  }

  throw new Error(message);
}

function normalizeError(error, fallbackMessage) {
  if (error.name === "AbortError") {
    return new Error("Request timed out. Please check the backend connection.");
  }

  return new Error(error.message || fallbackMessage);
}

function normalizeQuestionsPayload(data, fallback = {}) {
  const fallbackPassages = resolveTypingPassages(fallback.typingPassages);
  const fallbackLimit = Number(fallback.questionLimit) || 10;

  if (Array.isArray(data)) {
    return {
      questions: data,
      questionLimit: fallbackLimit,
      typingPassages: fallbackPassages,
    };
  }

  const rawPassages = Array.isArray(data?.typingPassages) ? data.typingPassages : [];
  return {
    questions: data?.questions ?? [],
    questionLimit: Number(data?.questionLimit) || fallbackLimit,
    typingPassages: rawPassages.length
      ? rawPassages
      : fallbackPassages,
  };
}

export async function fetchQuestions() {
  try {
    const response = await fetchWithTimeout("/api/questions");
    const data = await parseResponse(response, "Failed to load questions");
    return normalizeQuestionsPayload(data);
  } catch (error) {
    throw normalizeError(error, "Failed to load questions");
  }
}

export async function saveQuestions(questions, questionLimit, typingPassages) {
  const payload = { questions, questionLimit, typingPassages };
  try {
    const response = await fetchWithTimeout(
      "/api/questions",
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      },
      SAVE_TIMEOUT_MS
    );

    const data = await parseResponse(response, "Failed to save questions");
    return normalizeQuestionsPayload(data, payload);
  } catch (error) {
    throw normalizeError(error, "Failed to save questions");
  }
}

export async function fetchResults() {
  try {
    const response = await fetchWithTimeout("/api/results");
    return parseResponse(response, "Failed to load results");
  } catch (error) {
    throw normalizeError(error, "Failed to load results");
  }
}

export async function checkPhoneSubmitted(phone) {
  try {
    const response = await fetchWithTimeout(`/api/results?phone=${encodeURIComponent(phone)}`);
    const data = await parseResponse(response, "Failed to check phone number");
    return Boolean(data.exists);
  } catch (error) {
    throw normalizeError(error, "Failed to check phone number");
  }
}

export async function submitResult(result) {
  try {
    const response = await fetchWithTimeout("/api/results", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(result),
    });

    return parseResponse(response, "Failed to save result");
  } catch (error) {
    throw normalizeError(error, "Failed to save result");
  }
}
